import {
  Component,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { RouterOutlet, RouterLink, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { AuthService } from '../../../features/auth/services/auth.service';
import { ThemeService } from '../../../core/services/theme.service';
import { AsyncPipe } from '@angular/common';
import { UserInfoComponent } from '../user-info/user-info.component';
import { MatMenuModule } from '@angular/material/menu';
import {
  NavigationService,
  NavigationItem,
} from '../../../core/services/navigation.service';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDividerModule } from '@angular/material/divider';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    RouterOutlet,
    RouterLink,
    MatButtonModule,
    AsyncPipe,
    UserInfoComponent,
    MatMenuModule,
    MatExpansionModule,
    MatDividerModule,
  ],
  template: `
    <mat-toolbar class="admin-toolbar">
      <button
        mat-icon-button
        type="button"
        (click)="sidenav.toggle()"
        aria-label="Toggle navigation"
      >
        <mat-icon>menu</mat-icon>
      </button>
      <a routerLink="/dashboard" class="brand" aria-label="EngineersInsight Admin home">
        <img
          src="assets/engineer-insight-icon-only.png"
          alt=""
          class="app-logo"
        />
        <span class="brand__text">
          <span class="brand__name">EngineersInsight</span>
          <span class="brand__badge">Admin</span>
        </span>
      </a>
      <span class="spacer"></span>
      <button
        mat-icon-button
        class="theme-toggle-btn"
        type="button"
        (click)="toggleTheme()"
        aria-label="Toggle theme"
      >
        <mat-icon>{{
          (isDarkMode$ | async) ? 'light_mode' : 'dark_mode'
        }}</mat-icon>
      </button>
      <button
        mat-icon-button
        type="button"
        [matMenuTriggerFor]="userMenu"
        aria-label="User menu"
      >
        <mat-icon>account_circle</mat-icon>
      </button>
      <mat-menu #userMenu="matMenu" class="user-menu">
        <div class="user-menu-header">
          @if (authService.authState$ | async; as user) {
            <p>{{ user.email }}</p>
          }
        </div>
        <div class="user-menu-content">
          <app-user-info></app-user-info>
        </div>
        <mat-divider></mat-divider>
        <button mat-menu-item type="button" (click)="logout()">
          <mat-icon>exit_to_app</mat-icon>
          <span>Logout</span>
        </button>
        @if (showDebugInfo) {
          <button mat-menu-item type="button" routerLink="/debug">
            <mat-icon>bug_report</mat-icon>
            <span>Debug Auth</span>
          </button>
        }
      </mat-menu>
    </mat-toolbar>

    <mat-sidenav-container class="admin-shell">
      <mat-sidenav
        #sidenav
        mode="side"
        [opened]="true"
        class="admin-sidenav"
      >
        <mat-nav-list>
          @for (item of navigationService.navItems$ | async; track item.link || item.title) {
            @if (!item.children || item.children.length === 0) {
              <a
                mat-list-item
                [routerLink]="item.link"
                routerLinkActive="active-link"
              >
                <mat-icon matListItemIcon>{{ item.icon }}</mat-icon>
                <span
                  matListItemTitle
                  [attr.data-testid]="getNavTestId(item)"
                  >{{ item.title }}</span
                >
              </a>
            }
          }
        </mat-nav-list>
      </mat-sidenav>

      <mat-sidenav-content class="admin-content">
        <router-outlet></router-outlet>
      </mat-sidenav-content>
    </mat-sidenav-container>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .admin-toolbar {
        background: var(--color-bg-card);
        color: var(--color-fg);
        border-bottom: 1px solid var(--color-border);
        height: 56px;
        padding: 0 0.75rem 0 0.35rem;
        gap: 0.25rem;
      }

      .brand {
        display: inline-flex;
        align-items: center;
        gap: 0.65rem;
        text-decoration: none;
        color: inherit;
        min-width: 0;
      }

      .brand__text {
        display: flex;
        flex-direction: column;
        line-height: 1.1;
        min-width: 0;
      }

      .brand__name {
        font-weight: 700;
        font-size: 0.95rem;
        letter-spacing: -0.03em;
        white-space: nowrap;
      }

      .brand__badge {
        font-size: 0.65rem;
        font-weight: 600;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: var(--color-accent);
      }

      .app-logo {
        height: 28px;
        width: auto;
        margin: 0;
        flex-shrink: 0;
      }

      .spacer {
        flex: 1 1 auto;
      }

      .admin-shell {
        height: calc(100vh - 56px);
        background: var(--color-bg);
      }

      .admin-sidenav {
        width: 240px;
        background: var(--color-bg-card);
        border-right: 1px solid var(--color-border);
      }

      mat-nav-list {
        padding: 0.75rem 0 1.25rem;
      }

      .admin-content {
        background: var(--color-bg);
      }

      .user-menu-header {
        padding: 8px 16px;
        font-size: 14px;
        color: var(--color-fg-muted);
        border-bottom: 1px solid var(--color-border);
      }

      .user-menu-content {
        padding: 0;
      }

      ::ng-deep .user-menu {
        max-width: none !important;
        width: 400px;
      }

      ::ng-deep .user-menu .mat-mdc-menu-content {
        padding: 0;
      }

      @media (max-width: 720px) {
        .brand__name {
          display: none;
        }
      }
    `,
  ],
})
export class LayoutComponent implements OnInit {
  authService = inject(AuthService);
  private router = inject(Router);
  private themeService = inject(ThemeService);
  navigationService = inject(NavigationService);

  isDarkMode$ = this.themeService.isDarkMode$;
  showDebugInfo = environment.showDebugInfo;

  ngOnInit(): void {}

  getNavTestId(item: NavigationItem): string {
    switch (item.link) {
      case '/dashboard':
        return 'nav-dashboard-link';
      case '/content':
        return 'nav-content-link';
      case '/jobs':
        return 'nav-jobs-link';
      case '/adverts':
        return 'nav-adverts-link';
      case '/manufacturers':
        return 'nav-manufacturers-link';
      case '/product-categories':
        return 'nav-product-categories-link';
      case '/industries':
        return 'nav-industries-link';
      case '/organisations':
        return 'nav-organisations-link';
      case '/users':
        return 'nav-users-link';
      default:
        return (
          'nav-' +
          (item.title || '').toLowerCase().replace(/\s+/g, '-') +
          '-link'
        );
    }
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  logout(): void {
    console.log('Logout button clicked');
    this.router.navigate(['/login']).then(() => {
      this.authService
        .signOut()
        .then(() => {
          console.log('Logout successful');
        })
        .catch((error: Error) => {
          console.error('Error during logout:', error);
          window.location.reload();
        });
    });
  }
}
